import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AddFinalGrades } from 'app/library/final-grade/actions';
import { FinalGrade } from 'app/library/final-grade/models/final-grade.model';
import { FinalGradeState } from 'app/library/final-grade/models/final-grade.state';
import { AddGradedUnits } from 'app/library/graded-unit/actions';
import { IGradedUnitQuery } from 'app/library/graded-unit/interfaces/graded-unit.query.interface';
import { GradedUnit } from 'app/library/graded-unit/models/graded-unit.model';
import { GradingScheme } from 'app/library/grading-scheme/models/grading-scheme.model';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import { SetModulePartialGrades } from 'app/library/module-partial-grades/actions';
import { PartialGrade } from 'app/library/partial-grade/models/partial-grade.model';
import { SetParticipantPartialGrades } from 'app/library/participant-partial-grades/actions';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { map } from 'rxjs/operators';
import {
    DeselectSelectedModuleFinalGrades,
    FetchModuleFinalGrades,
    FetchModuleFinalGradesFailure,
    FetchModuleFinalGradesForParticipant,
    FetchModuleFinalGradesForParticipantFailure,
    FetchModuleFinalGradesForParticipantSuccess,
    FetchModuleFinalGradesSuccess,
    SearchModuleFinalGrades,
    SelectFilteredModuleFinalGrades,
    SetModuleFinalGrades,
    SortModuleFinalGrades,
    ToggleSelectedModuleFinalGrade,
    UnsetModuleFinalGrades,
} from '../actions';
import { ModuleFinalGradesService } from '../services/module-final-grades.service';
import { ModuleFinalGradesStateModel } from './module-final-grades.state-model';

@State<ModuleFinalGradesStateModel>({
    name: 'moduleFinalGrades',
    defaults: new ModuleFinalGradesStateModel(),
})
@Injectable()
export class ModuleFinalGradesState extends SubentityState<FinalGrade> {
    @Selector([FinalGradeState.getEntitiesAsMap, GradingSchemeState.getEntity])
    static getEntities(state: ModuleFinalGradesStateModel, finalGrades: Map<string, FinalGrade>, gradingScheme: GradingScheme): FinalGrade[] {
        if (!finalGrades) {
            return null;
        }

        const toReturn = state.list.map((id) => finalGrades.get(id));

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<FinalGrade>((finalGrade) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGrade, { grade: gradesContainer[finalGrade.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([FinalGradeState.getEntities, GradingSchemeState.getEntity, UserState.getEntities])
    static getEntitiesWithUser(state: ModuleFinalGradesStateModel, finalGrades: Map<string, FinalGrade>, gradingScheme: GradingScheme, users: User[]): FinalGrade[] {
        const toReturn = ModuleFinalGradesState.getEntities(state, finalGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<FinalGrade>((finalGrade) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGrade, { user: usersContainer[finalGrade.user.id] || null })
            );
        }

        return toReturn;
    }

    @Selector([FinalGradeState.getEntities, GradingSchemeState.getEntity])
    static getFilteredEntities(state: ModuleFinalGradesStateModel, finalGrade: FinalGrade[], gradingScheme: GradingScheme): FinalGrade[] {
        if (!finalGrade) {
            return null;
        }

        const toReturn = finalGrade.filter((finalGradeTmp) => state.filtered.indexOf(finalGradeTmp.id) !== -1);

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<FinalGrade>((finalGradeTmp) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGradeTmp, { grade: gradesContainer[finalGradeTmp.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([FinalGradeState.getEntities, GradingSchemeState.getEntity, UserState.getEntities])
    static getFilteredEntitiesWithUser(state: ModuleFinalGradesStateModel, finalGrades: FinalGrade[], gradingScheme: GradingScheme, users: User[]): FinalGrade[] {
        const toReturn = ModuleFinalGradesState.getFilteredEntities(state, finalGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<FinalGrade>((finalGrade) =>
                Object.assign<FinalGrade, FinalGrade, Partial<FinalGrade>>(new FinalGrade(), finalGrade, { user: usersContainer[finalGrade.user.id] || null })
            );
        }

        return toReturn;
    }

    @Selector()
    static getSelectedEntityIds(state: ModuleFinalGradesStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getFiltersList(state: ModuleFinalGradesStateModel): string[] {
        return state.filters.list;
    }

    @Selector()
    static getSearchTerm(state: ModuleFinalGradesStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: ModuleFinalGradesStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: ModuleFinalGradesStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: ModuleFinalGradesStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getPageSize(state: ModuleFinalGradesStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: ModuleFinalGradesStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: ModuleFinalGradesStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    constructor(private _moduleFinalGradesService: ModuleFinalGradesService, private _store: Store) {
        super(null, 30);

        this._propstoSearch = [...FinalGrade.props_to_search];
    }

    @Action(SetModuleFinalGrades)
    setAll(ctx: StateContext<ModuleFinalGradesStateModel>, action: SetModuleFinalGrades): void {
        this._setSubentities(ctx, action, AddFinalGrades);
    }

    @Action(UnsetModuleFinalGrades)
    unsetAll(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchModuleFinalGrades)
    fetchAll(ctx: StateContext<ModuleFinalGradesStateModel>, action: FetchModuleFinalGrades): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetModuleFinalGrades,
            FetchModuleFinalGradesSuccess,
            FetchModuleFinalGradesFailure,
            this._moduleFinalGradesService
                .findGrades(action.payload.moduleId, action.payload.search, action.payload.sortBy, action.payload.sortOrder, action.payload.pageSize, action.payload.pageNumber)
                .pipe(
                    map((resp) => {
                        ctx.patchState({
                            totalCount: resp.pagination.number_of_results,
                        });
                        // GradeParticipantQuery, and model
                        const partialGrades: PartialGrade[] = [];
                        resp.data.map((query) => {
                            if (query.partial_grades) {
                                query.partial_grades.map((partialGrade) => {
                                    partialGrade.participant = query.participant;
                                    partialGrades.push(new PartialGrade(partialGrade));
                                });
                            }
                        });

                        this._store.dispatch(new SetModulePartialGrades(partialGrades));

                        return resp.data.map((query) => {
                            query.final_grade.participant = query.participant;
                            return new FinalGrade(query.final_grade);
                        });
                    })
                )
        );
    }

    @Action(FetchModuleFinalGradesSuccess)
    fetchAllSuccess(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchModuleFinalGradesFailure)
    fetchAllFailure(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchModuleFinalGradesForParticipant)
    fetchAllForParticipant(ctx: StateContext<ModuleFinalGradesStateModel>, action: FetchModuleFinalGradesForParticipant): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetModuleFinalGrades,
            FetchModuleFinalGradesForParticipantSuccess,
            FetchModuleFinalGradesForParticipantFailure,
            this._moduleFinalGradesService.findModuleFinalGradesForParticipant(action.payload.moduleId, action.payload.participantId).pipe(
                map((resp) => {
                    const gradedUnitsConatiner: { [key: string]: GradedUnit } = {};

                    ctx.patchState({
                        totalCount: resp.pagination.number_of_results,
                    });
                    const partialGrades: PartialGrade[] = [];
                    resp.data.map((query) => {
                        if (query.partial_grades) {
                            query.partial_grades.map((partialGrade) => {
                                partialGrade.participant = query.participant;
                                partialGrades.push(new PartialGrade(partialGrade));
                            });

                            query.partial_grades.forEach((query) => {
                                if (!gradedUnitsConatiner[query.graded_unit.id]) {
                                    const gradedUnitQuery = query.graded_unit as IGradedUnitQuery;

                                    gradedUnitsConatiner[query.graded_unit.id] = new GradedUnit(gradedUnitQuery);
                                }
                            });
                        }
                    });

                    const gradedUnits = Object.keys(gradedUnitsConatiner).map((gradedUnitId) => gradedUnitsConatiner[gradedUnitId]);

                    this._store.dispatch([new AddGradedUnits(gradedUnits), new SetParticipantPartialGrades(partialGrades), new FetchModuleFinalGradesForParticipantSuccess()]);

                    return resp.data.map((query) => {
                        query.final_grade.participant = query.participant;
                        return new FinalGrade(query.final_grade);
                    });
                })
            )
        );
    }

    @Action(FetchModuleFinalGradesForParticipantSuccess)
    fetchAllSuccessForParticipant(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchModuleFinalGradesForParticipantFailure)
    fetchAllFailureForParticipant(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedModuleFinalGrade)
    toggleSelect(ctx: StateContext<ModuleFinalGradesStateModel>, action: ToggleSelectedModuleFinalGrade): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModuleFinalGrades)
    selectAll(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModuleFinalGrades)
    deselectAll(ctx: StateContext<ModuleFinalGradesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchModuleFinalGrades)
    search({ patchState }: StateContext<ModuleFinalGradesStateModel>, { payload }: SearchModuleFinalGrades): void {
        sessionStorage.setItem('finalGradesSearchTerm', payload);
        patchState({
            searchTerm: payload,
        });
    }

    @Action(SortModuleFinalGrades)
    sort({ patchState }: StateContext<ModuleFinalGradesStateModel>, { payload }: SortModuleFinalGrades): void {
        sessionStorage.setItem('moduleFinalGradesSortBy', payload.active);
        sessionStorage.setItem('moduleFinalGradesSortOrder', payload.direction);

        patchState({
            sortBy: payload.active,
            sortOrder: payload.direction,
        });
    }
}
